/**
 * chime — short synthesized audio cues for the guided voice tour.
 *
 * The tour advances on its own, so the operator often isn't looking at the
 * screen. These cues tell them where they are without reading: the mic just
 * opened, a value was accepted, an optional step was skipped.
 *
 * No audio files and no library — three oscillators through the Web Audio API.
 *
 * A single AudioContext is shared across every chime. Creating one per call
 * leaks: browsers cap concurrent contexts (Chrome allows roughly six) and the
 * guided flow plays a cue on every one of its 27 steps, so a per-call context
 * would go silent partway through the form.
 */

let ctx = null;

/** Lazily create (and revive) the shared AudioContext. Returns null if unsupported. */
function getContext() {
  if (typeof window === 'undefined') return null;
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  if (!ctx) {
    try {
      ctx = new AudioContextClass();
    } catch {
      return null;
    }
  }

  // Browsers suspend contexts created before a user gesture, and may suspend
  // them again when the tab is backgrounded. Reviving is cheap and idempotent.
  if (ctx.state === 'suspended') {
    try { ctx.resume(); } catch { /* noop */ }
  }

  return ctx;
}

/**
 * Play a cue.
 * @param {'start'|'success'|'skip'} type
 *   'start'   — rising 440→880 Hz: the mic is now listening
 *   'success' — double tap 880→1109.73 Hz: value accepted, moving on
 *   'skip'    — falling 330→261.63 Hz: optional step skipped
 */
export function playChime(type) {
  const audio = getContext();
  if (!audio) return;

  try {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    osc.connect(gain);
    gain.connect(audio.destination);

    const now = audio.currentTime;

    if (type === 'start') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.15);
      gain.gain.setValueAtTime(0.1, now);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.15);
      osc.start(now);
      osc.stop(now + 0.15);
    } else if (type === 'success') {
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.setValueAtTime(1109.73, now + 0.08);
      gain.gain.setValueAtTime(0.08, now);
      gain.gain.setValueAtTime(0.08, now + 0.08);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else if (type === 'skip') {
      osc.type = 'sine';
      osc.frequency.setValueAtTime(330, now);
      osc.frequency.setValueAtTime(261.63, now + 0.1);
      gain.gain.setValueAtTime(0.05, now);
      gain.gain.setValueAtTime(0.05, now + 0.1);
      gain.gain.linearRampToValueAtTime(0.001, now + 0.25);
      osc.start(now);
      osc.stop(now + 0.25);
    } else {
      return;
    }

    // Release the nodes once the tone has finished so a long tour doesn't
    // accumulate them on the shared context.
    osc.onended = () => {
      try { osc.disconnect(); gain.disconnect(); } catch { /* noop */ }
    };
  } catch (e) {
    // Audio is a nicety; never let it break the tour.
    console.warn('Audio chime failed to play:', e);
  }
}
